/**
 * API Logger Utility
 * 
 * A simple, focused utility for logging and analyzing API responses
 * without needing a dedicated API debugger page.
 */

import { findResultsObjects, extractErrorInfo } from './apiResponseAnalyzer';

/**
 * API Logger class - provides methods for logging API responses and results
 */
export class ApiLogger {
  /**
   * Log an API response to the console with results detection
   * 
   * @param source - Source of the API call (endpoint or component name)
   * @param response - The API response data to log
   */
  logResponse(source: string, response: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.group(`📊 API Response [${source}]`);
      
      // Log basic response info
      console.log('Response data:', response);
      
      // Check if there's an error in the response
      const errorInfo = extractErrorInfo(response);
      if (errorInfo) {
        console.error('Error detected in response:', errorInfo);
      }
      
      // Find any Results objects in the response
      const resultsObjects = findResultsObjects(response);
      if (resultsObjects.length > 0) {
        console.log(`Found ${resultsObjects.length} Results objects:`);
        resultsObjects.forEach((result, index) => {
          console.group(`Results Object #${index + 1} (Path: ${result.path})`);
          console.log('Type:', result.type);
          console.log('Data:', result.data);
          console.groupEnd();
        });
      } else {
        console.log('No Results objects found in the response');
      }
      
      console.groupEnd();
    }
  }
  
  /**
   * Log debug information (only in development)
   */
  debug(message: string, data?: any): void {
    if (process.env.NODE_ENV === 'development') {
      console.group(`🔍 Debug: ${message}`);
      if (data) console.log(data);
      console.groupEnd();
    }
  }
  
  /**
   * Log a message regardless of environment
   */
  log(message: string, data?: any): void {
    console.group(`📋 ${message}`);
    if (data) console.log(data);
    console.groupEnd();
  }
  
  /**
   * Log error information
   */
  error(message: string, error?: any): void {
    console.group(`❌ Error: ${message}`);
    if (error) console.error(error);
    console.groupEnd();
  }
  
  /**
   * Log warning information
   */
  warn(message: string, data?: any): void {
    console.group(`⚠️ Warning: ${message}`);
    if (data) console.warn(data);
    console.groupEnd();
  }
  
  /**
   * Analyze an API response for key data points
   * 
   * @param response - The API response to analyze
   * @returns Structured analysis of the response
   */
  analyzeResponse(response: any): { 
    hasResults: boolean;
    resultsCount: number;
    resultsData: any[];
    hasError: boolean;
    error: any | null;
  } {
    const resultsObjects = findResultsObjects(response);
    const errorInfo = extractErrorInfo(response);
    
    return {
      hasResults: resultsObjects.length > 0,
      resultsCount: resultsObjects.length,
      resultsData: resultsObjects.map(r => r.data),
      hasError: !!errorInfo,
      error: errorInfo
    };
  }
}

// Create and export a singleton instance
export const apiLogger = new ApiLogger();

// Default export for convenience
export default apiLogger;
